/**
 * RenderDebugOverlay — a small live HUD (bottom-right) that shows what the 3D
 * scene is doing (framerate, draw calls, triangles, GPU memory) and lets you pin
 * the NOTE and OBSTACLE draw distances (FOV) to manual values, independently.
 *
 * Reads `window.__synapsePerf` (mutated every frame by AdaptivePerfGovernor) on a
 * light 250ms interval. All controls (open state + both manual toggles/values)
 * persist to localStorage, so they survive a browser refresh. Toggle the panel
 * with the backtick (`) key or the × button. Mounted only in the SR editor.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color } from '../ui/theme';

interface Probe {
  fps: number; calls: number; triangles: number;
  geometries: number; textures: number;
  maxNotes: number; maxObstacles: number;
  noteViewBeats: number; obstacleViewBeats: number; maxNotesVR: number; scale: number;
  obsVisible: number; obsMounted: number; notesDrawn: number; rails: number;
}

const fmt = (n: number): string =>
  n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M'
  : n >= 1000 ? (n / 1000).toFixed(1) + 'k'
  : String(Math.round(n));

// ── Persistence ──────────────────────────────────────────────────────────────
const LS_KEY = 'synapse_render_debug_v1';
interface Saved {
  open?: boolean; noteManual?: boolean; noteFov?: number; obsManual?: boolean; obsFov?: number;
  pos?: { x: number; y: number };
}
function loadSaved(): Saved {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') as Saved; } catch { return {}; }
}
function persist(s: Saved): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch { /* quota */ }
}

const distColor = (v: number): string => (v >= 44 ? '#34D399' : v >= 26 ? '#FBBF24' : '#F87171');

export default function RenderDebugOverlay() {
  const saved = useMemo(() => loadSaved(), []);
  // Panel open state lives in the store so the transport button can toggle it
  // (and light up when it's open). The FOV controls stay local + persisted here.
  const open = useEditorStore(s => s.fovPanelOpen);
  const setOpen = useEditorStore(s => s.setFovPanelOpen);
  const [probe, setProbe] = useState<Probe | null>(null);
  const [beat, setBeat] = useState(0);

  // Draggable position (null = default bottom-right).
  const [pos, setPos] = useState<{ x: number; y: number } | null>(saved.pos ?? null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  // Object density / draw distance is now driven by the PerfPill (perfMode); this
  // panel is a read-only diagnostic HUD. Show the active mode below.
  const perfMode = useEditorStore(s => s.perfMode);

  // Persist the panel position across refreshes.
  useEffect(() => { persist({ pos: pos ?? undefined }); }, [pos]);

  // ── Drag the panel by its header ──
  const onDrag = useCallback((e: MouseEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const pw = panelRef.current?.offsetWidth ?? 200;
    const ph = panelRef.current?.offsetHeight ?? 300;
    setPos({
      x: Math.max(0, Math.min(window.innerWidth - pw, e.clientX - d.dx)),
      y: Math.max(0, Math.min(window.innerHeight - ph, e.clientY - d.dy)),
    });
  }, []);
  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', onDragEnd);
  }, [onDrag]);
  const onHeaderDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;   // don't drag from the × button
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setPos({ x: rect.left, y: rect.top });                     // switch to left/top anchoring
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', onDragEnd);
    e.preventDefault();
  }, [onDrag, onDragEnd]);

  // Poll the live probe + current beat (light, off the render loop).
  useEffect(() => {
    const id = window.setInterval(() => {
      const p = (window as unknown as { __synapsePerf?: Probe }).__synapsePerf;
      setProbe(p ? { ...p } : null);
      setBeat(useEditorStore.getState().playback.currentBeat);
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  // Backtick toggles the panel (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '`' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const st = useEditorStore.getState();
      st.setFovPanelOpen(!st.fovPanelOpen);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Closed → render nothing; the transport button (and backtick) reopen it.
  if (!open) return null;

  const fps = probe?.fps ?? 0;
  const fpsColor = fps >= 55 ? '#34D399' : fps >= 40 ? '#FBBF24' : '#F87171';
  const noteView = probe?.noteViewBeats ?? 0;
  const obsView = probe?.obstacleViewBeats ?? 0;

  const style: React.CSSProperties = pos
    ? { ...panelStyle, left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
    : panelStyle;

  return (
    <div ref={panelRef} style={style}>
      <div style={{ ...headerStyle, cursor: 'move' }} onMouseDown={onHeaderDown} title="Drag to move">
        <span>RENDER · SCENE</span>
        <button onClick={() => setOpen(false)} style={closeStyle} title="Hide ( ` )">×</button>
      </div>

      <Row label="FPS" value={String(Math.round(fps))} valColor={fpsColor} big />
      <Row label="Draw calls" value={fmt(probe?.calls ?? 0)} />
      <Row label="Triangles" value={fmt(probe?.triangles ?? 0)} />
      <Row label="Geometries" value={fmt(probe?.geometries ?? 0)} />
      <Row label="Textures" value={fmt(probe?.textures ?? 0)} />

      <div style={dividerStyle} />

      <Row label="Note dist (FOV)" value={`${noteView} beats`} valColor={distColor(noteView)} />
      <Row label="Obstacle dist" value={`${obsView} beats`} valColor={distColor(obsView)} />
      <Row label="Obstacles vis/mnt" value={`${probe?.obsVisible ?? 0} / ${probe?.obsMounted ?? 0}`} />
      <Row label="Notes drawn" value={fmt(probe?.notesDrawn ?? 0)} />
      <Row label="Rails" value={fmt(probe?.rails ?? 0)} />
      <Row label="Beat" value={beat.toFixed(1)} />

      <div style={barTrackStyle}>
        <div style={{ ...barFillStyle, width: `${Math.round((probe?.scale ?? 0) * 100)}%` }} />
      </div>

      {/* Object density is controlled by the PerfPill — show the active mode. */}
      <div style={dividerStyle} />
      <Row label="Perf mode" value={perfMode} valColor="#A78BFA" />
    </div>
  );
}

function Row({ label, value, valColor, big }: {
  label: string; value: string; valColor?: string; big?: boolean;
}) {
  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={{ ...valueStyle, color: valColor || '#E5E7EB', fontSize: big ? 15 : 11.5 }}>{value}</span>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const panelStyle: React.CSSProperties = {
  // Default anchor: just right of the left sidebar, near the transport controls
  // at the sidebar's bottom (draggable from here; position persists).
  position: 'fixed',
  left: 'calc(var(--shell-pad-left, 12px) + var(--sr-left-w, 230px) + 12px)',
  bottom: 96, zIndex: 50,
  width: 200, padding: '9px 11px 11px', borderRadius: 12,
  background: 'oklch(0.12 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.88)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.32)',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 8px 30px rgba(0,0,0,0.45)',
  fontFamily: MONO, color: '#E5E7EB', userSelect: 'none',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
  color: color.inkDim, marginBottom: 6,
};

const closeStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: color.inkFaint, cursor: 'pointer',
  fontSize: 16, lineHeight: 1, padding: 0,
};

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '1.5px 0',
};
const labelStyle: React.CSSProperties = { fontSize: 10.5, color: color.inkFaint };
const valueStyle: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 600 };

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
  color: color.inkFaint, marginBottom: 2,
};

const dividerStyle: React.CSSProperties = {
  height: 1, margin: '6px 0',
  background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
};

const barTrackStyle: React.CSSProperties = {
  height: 4, marginTop: 8, borderRadius: 3,
  background: 'oklch(0.30 calc(0.03 * var(--tint-c)) var(--tint-h) / 0.5)', overflow: 'hidden',
};
const barFillStyle: React.CSSProperties = {
  height: '100%', borderRadius: 3,
  background: 'linear-gradient(90deg, var(--accent-soft, #A78BFA), var(--accent, #7C3AED))',
  transition: 'width 0.25s ease',
};
